import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useGetCurrentOrganisationBoards,
  useGetOrganisation,
} from 'api/organisations';
import { DataWrapper } from 'components/shared';
import { OrganisationViewWrapper } from 'components/Organisations';
import { DYNAMIC_URLS } from 'consts';
import type { BoardType, OrganisationType } from 'types';

import {
  BoardsLayout,
  BoardsLayoutContent,
  BoardsLayoutSidebar,
} from './BoardsLayout';
import { BoardsSidebar } from './BoardsSidebar/BoardsSidebar';
import { BoardViews } from './BoardsViews';

type Params = {
  organisationQuery: ReturnType<typeof useGetOrganisation>;
  boardsQuery: ReturnType<typeof useGetCurrentOrganisationBoards>;
  boardId: string | undefined;
};

type CombinedQueriesState = {
  data:
    | {
        organisation: OrganisationType;
        boards: BoardType[];
        selectedBoard: BoardType;
      }
    | undefined;
  isError: boolean;
  isLoading: boolean;
};

const getCombinedQueriesState = ({
  organisationQuery,
  boardsQuery,
  boardId,
}: Params): CombinedQueriesState => {
  const {
    data: organisation,
    isError: isOrganisationError,
    isInitialLoading: isOrganisationInitialLoading,
  } = organisationQuery;
  const {
    data: boardsData,
    isError: isBoardsError,
    isLoading: isBoardsLoading,
  } = boardsQuery;
  const { data: boards } = boardsData || { data: undefined };

  const state: CombinedQueriesState = {
    data: undefined,
    isError: false,
    isLoading: true,
  };
  if (!organisation || !boards || !boardId) return state;

  const selectedBoard = boards.find(({ id }) => id.toString() === boardId);

  if (!selectedBoard) return state;

  return {
    data: { organisation, boards, selectedBoard },
    isError: false,
    isLoading: false,
  };
};

export const OrganisationBoards = () => {
  const { t } = useTranslation('organisations');
  const navigate = useNavigate();
  const { organisationId, boardId, settingsTabKey } = useParams() as {
    organisationId: string;
    boardId: string | undefined;
    settingsTabKey: string | undefined;
  };

  const organisationQuery = useGetOrganisation({
    pathParams: { organisationId },
  });
  const boardsQuery = useGetCurrentOrganisationBoards({
    pathParams: { organisationId },
  });
  const { data, isError, isLoading } = getCombinedQueriesState({
    organisationQuery,
    boardsQuery,
    boardId,
  });

  const tabsStateDetermined = Boolean(boardId && settingsTabKey);

  useEffect(() => {
    if (!tabsStateDetermined && boardsQuery.data) {
      let urlBoardId = boardId;

      if (!urlBoardId) {
        const { data: boards } = boardsQuery.data;
        if (boards.length === 0) throw new Error('Boards are empty');
        urlBoardId = boards[0].id.toString();
      }

      navigate(
        DYNAMIC_URLS.organisationBoardSettings({
          organisationId,
          boardId: urlBoardId,
          settingsTabKey: 'general-settings',
        }),
      );
    }
  }, [tabsStateDetermined, boardsQuery]);

  return (
    <DataWrapper
      data={data}
      isError={isError}
      isLoading={isLoading}
      errorMargin="xl"
    >
      {({ organisation, boards, selectedBoard }) => (
        <OrganisationViewWrapper organisation={organisation}>
          <BoardsLayout>
            <BoardsLayoutSidebar>
              <BoardsSidebar boards={boards} selectedBoard={selectedBoard} />
            </BoardsLayoutSidebar>
            <BoardsLayoutContent>
              <BoardViews board={selectedBoard} />
            </BoardsLayoutContent>
          </BoardsLayout>
        </OrganisationViewWrapper>
      )}
    </DataWrapper>
  );
};
